// MigrationPanel — Jun 2026 (Randy). The "Export here → Import there" data
// bridge for the Go Live Wizard. Preview and production are walled-off databases
// that can't talk directly, so we move the data as a downloaded file:
//   1. In PREVIEW: press Export → a JSON file downloads (silos + images + docs).
//   2. Deploy your code.
//   3. In PRODUCTION: press Import → upload that file → your silos appear.
// Keys are NEVER exported or overwritten — your live keys stay exactly as they are.
import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DownloadSimple, UploadSimple, Key, Warning, CheckCircle, Database, CaretRight, CaretDown, Copy, Check, ArrowsClockwise, MagnifyingGlass, Clock } from "@phosphor-icons/react";

// Universal capture-refresh prompt (Randy). Tucked away with a Copy button so
// Randy can paste it back into the agent chat after new feature rounds/POs to
// re-audit + extend what this export tool captures. Keep in sync with the
// registries it names.
//
// Aug 2026 (Randy pt-mig-credential-move) — trimmed from ~1,200 words to
// the essentials. Randy asked for a short version.
// Feb 17, 2026 (Randy pt-migration-polish) — two clarity refinements:
//   1. Named SILO_EXPORT_DROP_FIELDS explicitly so a reviewer doesn't
//      miss the fifth backend registry when new Stripe mode-scoped
//      fields land on the silo doc.
//   2. Broadened the sweep scope to include new fields on ANY already-
//      listed collection (not just the super-admin user doc) — silos,
//      documents, etc.
const CAPTURE_REFRESH_PROMPT = `Migration Capture Refresh — audit and update my Go-Live data export/import tool.

Source-of-truth registries:
- backend/server.py: MIGRATION_COLLECTIONS, MIGRATION_PROFILE_FIELDS, MIGRATION_PLATFORM_SETTINGS_DOCS, SILO_EXPORT_DROP_FIELDS
- frontend/src/components/MigrationPanel.jsx: LABELS, INCLUDED_GROUPS, NOT_INCLUDED

Do a full-repo sweep of every db.<collection> insert/update/upsert, every distinct platform_settings doc identity, and every new field written to my super-admin user document OR to any already-listed collection (silos, documents, etc.) since the last update. Diff against the registries and report every item that is persisted but NOT exported.

For each missing item that SHOULD travel: add it to MIGRATION_COLLECTIONS with the correct scope (silo / user / user_owner / fan_silo / singleton / all / user_or_seed) and an idempotent upsert match-key (single field or a tuple for compound identity); add safe platform_settings docs to MIGRATION_PLATFORM_SETTINGS_DOCS with Stripe/price/product IDs scrubbed; whitelist fields into MIGRATION_PROFILE_FIELDS; add any new Stripe or mode-scoped IDs on the silo doc to SILO_EXPORT_DROP_FIELDS so they get scrubbed on export; mirror everything into LABELS + INCLUDED_GROUPS. Heavy regenerable blobs (images, voice) also land in the export endpoint's LITE_SKIP set.

NEVER export: money balances (reservoir/vouchers/bank), earnings/ledgers/payout history, Stripe customer/subscription/price/product IDs, other users' data (subscriptions, opt-ins, buddy messages), login security (passwords, 2FA, referral codes, invite/handoff tokens), and — per Randy's current setup — the personal BYOK / platform site LLM keys (already configured on production).

After wiring, verify the /admin/migration/export -> /admin/migration/import round-trip stays additive + idempotent, and give me a before/after list of what was added.`;

export default function MigrationPanel() {
  const [exporting, setExporting] = useState(false);
  const [liteExport, setLiteExport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [lastExport, setLastExport] = useState(null);
  const [lastSize, setLastSize] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const [showIncluded, setShowIncluded] = useState(false);
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  // Feb 17, 2026 (Randy pt-migration-polish) — preview counts + last-run
  // timestamps let Randy eyeball what's about to move before pressing
  // Export, and see when this environment was last touched.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [state, setState] = useState({ last_import_at: null, last_export_at: null });
  const fileRef = useRef(null);

  useEffect(() => {
    // Load timestamps on mount so the panel header can render "Last
    // import on this environment: 3 days ago" etc.
    api.get("/admin/migration/state")
      .then(r => setState(r.data || {}))
      .catch(() => {});
  }, []);

  const timeAgo = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
    if (secs < 2592000) return `${Math.floor(secs / 86400)} day${Math.floor(secs / 86400) === 1 ? "" : "s"} ago`;
    return d.toLocaleDateString();
  };

  const copyRefreshPrompt = async () => {
    const text = CAPTURE_REFRESH_PROMPT;
    const legacyCopy = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed"; ta.style.top = "0"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        return ok;
      } catch { return false; }
    };
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); ok = true; }
    } catch { ok = false; }
    if (!ok) ok = legacyCopy();
    if (ok) {
      setCopiedPrompt(true);
      toast.success("Capture-refresh prompt copied — paste it into the agent chat.");
      setTimeout(() => setCopiedPrompt(false), 2500);
    } else {
      toast.error("Couldn't copy — select the text and copy manually.");
    }
  };

  // Plain-English names for each data type (Randy hates raw field names).
  const LABELS = {
    silos: "silos",
    documents: "documents",
    rag_chunks: "knowledge pieces (silo brain)",
    silo_face_images: "images (face + silo art)",
    workbench_states: "workbench setups",
    staging_question_bank: "staged questions",
    custom_commands: "custom commands",
    bookmarks: "bookmarks",
    bookmark_audio: "saved voice clips",
    chat_sessions: "remembered conversations",
    yt_video_libraries: "YouTube libraries",
    bump_configs: "discovery/bump settings",
    global_commands: "tool commands (Workbench + Refinery)",
    workbench_macros: "saved macros",
    email_templates: "email templates",
    llm_routing_config: "LLM routing (which model runs where)",
    llm_surface_tune: "LLM fine-tuning",
    bump_config_defaults: "discovery layout defaults",
    battle_archives: "saved Silo Wars battles",
    calendar_events: "calendar events (organizer)",
    kid_profiles: "kid profiles",
    family_chores: "chores library",
    family_rewards: "rewards store",
    family_chore_suggestions: "chore suggestion gallery",
    reward_redemptions: "reward receipts",
    theme_content_snapshots: "theme memory (packed-away content)",
    member_avatars: "member avatars",
    calendar_archives: "calendar month archives",
    calendar_prefs: "quiet-hours preferences",
    user_prefs: "per-silo view preferences",
    staging_alerts: "scheduled pings & reminders",
    // Aug 2026 (Randy pt-mig-credential-move) — new credential + module labels.
    youtube_integrations: "YouTube Reply connections (per silo × channel)",
    youtube_reply_drafts: "YT Reply drafts (your dig history)",
    youtube_post_all_jobs: "YT bulk-post job history",
    platform_settings: "platform settings & Creator Pro shop",
    admin_profile: "your profile & personal setup",
  };
  const label = (k) => LABELS[k] || k.replace(/_/g, " ");

  // What the export carries — grouped for the "What's included" disclosure.
  const INCLUDED_GROUPS = [
    {
      title: "Your silos & their brains",
      items: ["Every silo you own", "All documents (Tier 1/2/3)", "The searchable knowledge brain (incl. vectors — no re-processing)", "Face + silo-art images", "Workbench setups & staged questions", "Bookmarks, saved voice clips & your remembered conversations", "YouTube libraries", "Document origin tags (Workbench / Refinery / Impromptu Staging + the operation used)", "Your silos' subscription prices (the amounts — Stripe objects are re-created fresh on production)"],
    },
    {
      title: "Your AI Calendar & family organizer",
      items: ["Every calendar event, chore and reward in every silo you own", "Kid profiles (with their per-kid chore lists & availability)", "Reward receipts + the chore suggestion gallery", "Theme system: active theme words, theme history & each theme's packed-away content (including favorite-emojis pool + user-added describers + this silo's scheduled pings — they ride with the silo record + theme memory)", "Scheduled pings & reminders you've created (staging_alerts)", "Member avatars (skipped by Lite export, like other images)", "Your quiet hours & per-silo view preferences"],
    },
    {
      title: "Platform setup (so production matches preview)",
      items: ["The Workbench + File Refinery tool commands", "Your saved macros", "Email templates (yours + the built-ins)", "The Creator Pro shop (suites, prices, lock look) — the source of truth for the Creator Pro gate price too, so the Billing & Earnings slider follows it", "Default creator pricing & the dots/interaction system", "Platform flags (grace period, Silo Wars on/off)", "Fee settings (Stripe IDs removed)", "Which AI model powers each surface + fine-tuning", "Discovery/bump layout defaults", "Your saved Silo Wars battles", "Go-live safety thresholds"],
    },
    {
      title: "Your personal profile",
      items: ["Display name, bio & avatar", "Website & social handles (X, Instagram, TikTok, Twitch, YouTube)", "Preferred voice, cube navigation & display preferences", "Your per-silo UI toggles (reward store on/off, add-members drawer, etc.)", "Your LLM routing overrides & per-user threshold overrides", "Your Creator Pro entitlement (which SKUs you own)", "Your successor / beneficiary account setting", "Your Google Calendar OAuth connection (encrypted — decrypts on prod IF Fernet keys match; otherwise you re-connect once)"],
    },
    {
      title: "YouTube Replies + your dig history",
      items: ["YouTube Reply integrations per silo × channel (Client ID + encrypted Secret + encrypted OAuth refresh token — decrypts if Fernet keys match)", "Safety mode, monitored video lists, rate limits, confidence gate, custom reply signature — all your per-silo configuration", "Your YT Reply drafts (pending, posted, skipped, redrafted — full dig history preserved)", "Bulk-post job history"],
    },
  ];
  const NOT_INCLUDED = [
    "Your personal BYOK LLM keys and the platform site-key doc (you already have them on prod)",
    "Stripe price/product IDs, customers & subscriptions (belong to preview's Stripe account)",
    "Money balances — reservoir, vouchers & bank buckets",
    "Earnings, ledgers & payout history",
    "Other people's accounts, fans or their data",
    "Login security (passwords, 2FA)",
    "Family invite links & kid QR/magic-link tokens (credentials — regenerate on production)",
    "Push subscriptions & other people's ping/email opt-ins",
    "YTRE sessions & entries (skipped per Randy — not needed on prod day)",
  ];

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/admin/migration/export${liteExport ? "?lite=true" : ""}`);
      const bundle = r.data;
      const blob = new Blob([JSON.stringify(bundle)], { type: "application/json" });
      const mb = +(blob.size / (1024 * 1024)).toFixed(1);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `mysilo-data${liteExport ? "-lite" : ""}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastExport(bundle.counts || {});
      setLastSize(mb);
      // Refresh the last-export-at chip after a successful export.
      api.get("/admin/migration/state").then(r => setState(r.data || {})).catch(() => {});
      toast.success(`Exported ${bundle.silo_count} silo(s) · ${mb} MB${liteExport ? " (lite — no images/voice clips)" : ""}. File downloaded.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  // Feb 17, 2026 (Randy pt-migration-polish) — dry-run to show counts +
  // size before the user clicks Export. Uses the same collection queries
  // as the real export so the numbers are exact.
  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.get(`/admin/migration/preview-counts${liteExport ? "?lite=true" : ""}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  // Split a big export into payloads small enough to clear the ingress body
  // limit. Silos go first (content references them), then each collection in
  // size-capped batches. The import endpoint is additive + idempotent, so many
  // small POSTs land exactly like one big one — just without the 413.
  const MAX_BATCH_BYTES = 3 * 1024 * 1024; // ~3MB per POST
  const buildPayloads = (bundle) => {
    const colls = bundle.collections || {};
    const payloads = [];
    if (colls.silos?.length) payloads.push({ silos: colls.silos });
    for (const [coll, rows] of Object.entries(colls)) {
      if (coll === "silos" || !rows?.length) continue;
      let batch = [], size = 0;
      for (const row of rows) {
        const rs = JSON.stringify(row).length;
        if (batch.length && size + rs > MAX_BATCH_BYTES) {
          payloads.push({ [coll]: batch }); batch = []; size = 0;
        }
        batch.push(row); size += rs;
      }
      if (batch.length) payloads.push({ [coll]: batch });
    }
    return payloads;
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportProgress(null);
    try {
      const text = await file.text();
      let bundle;
      try { bundle = JSON.parse(text); }
      catch { throw new Error("That file isn't a valid My Silo export."); }
      if (!bundle.collections) throw new Error("That file isn't a valid My Silo export.");
      const payloads = buildPayloads(bundle);
      const totals = {};
      const allErrors = [];
      for (let i = 0; i < payloads.length; i++) {
        setImportProgress(`Uploading ${i + 1} / ${payloads.length}…`);
        const r = await api.post("/admin/migration/import", { bundle: { collections: payloads[i] } });
        for (const [k, v] of Object.entries(r.data?.imported || {})) {
          if (v > 0) totals[k] = (totals[k] || 0) + v;
        }
        if (r.data?.errors?.length) allErrors.push(...r.data.errors);
      }
      setLastImport({ imported: totals, errors: allErrors });
      const tot = Object.values(totals).reduce((a, b) => a + b, 0);
      toast.success(`Imported ${tot} record(s) across your silos.`);
      if (allErrors.length) toast.warning(`${allErrors.length} item(s) had issues — see details below.`);
      // Feb 17, 2026 (Randy pt-migration-polish) — refresh timestamps
      // so the "Last import" chip flips to "just now."
      api.get("/admin/migration/state").then(r => setState(r.data || {})).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Import failed.");
    } finally {
      setImporting(false);
      setImportProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border hairline rounded-xl p-5 shadow-sm" data-testid="migration-panel">
      <div className="flex items-center gap-2 mb-1">
        <Database size={18} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]" data-testid="migration-panel-title">Sync my content to production</h3>
      </div>
      {(state.last_export_at || state.last_import_at) && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[#6B6A68] mb-2" data-testid="migration-timestamps">
          {state.last_export_at && (
            <span className="flex items-center gap-1" data-testid="migration-last-export-at">
              <Clock size={11} weight="duotone" />
              Last export from here: <strong className="text-[#4A5D4E]">{timeAgo(state.last_export_at)}</strong>
            </span>
          )}
          {state.last_import_at && (
            <span className="flex items-center gap-1" data-testid="migration-last-import-at">
              <Clock size={11} weight="duotone" />
              Last import into here: <strong className="text-[#4A5D4E]">{timeAgo(state.last_import_at)}</strong>
            </span>
          )}
        </div>
      )}
      <p className="text-sm text-[#6B6A68] leading-relaxed mb-4">
        Your code deploys automatically — but your <strong>silos, their knowledge brain,
        images, and your saved voice/conversations live in the database</strong>, which is
        separate in production. Use this to carry it all across:
        <strong> Export here → deploy → Import there.</strong> The file rides on your computer; it is not part of the deploy.
      </p>

      {/* What's included — expandable */}
      <button
        type="button"
        onClick={() => setShowIncluded((v) => !v)}
        data-testid="migration-included-toggle"
        className="w-full flex items-center gap-1.5 text-sm font-medium text-[#4A5D4E] hover:text-[#3A4A3E] mb-3"
      >
        {showIncluded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        What's included
      </button>
      {showIncluded && (
        <div className="border hairline rounded-lg p-4 mb-4 bg-[#FBFAF6] space-y-3" data-testid="migration-included-panel">
          {INCLUDED_GROUPS.map((g, gi) => (
            <div key={gi} data-testid={`migration-included-group-${gi}`}>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">{g.title}</div>
              <ul className="text-[12px] text-[#1C1B1A] leading-relaxed space-y-0.5">
                {g.items.map((it, ii) => (
                  <li key={ii} className="flex items-start gap-1.5">
                    <CheckCircle size={13} weight="duotone" className="text-[#2F6B3A] mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="pt-2 border-t hairline" data-testid="migration-not-included">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#B8862F] mb-1">Never included (stays safe)</div>
            <ul className="text-[12px] text-[#6B6A68] leading-relaxed space-y-0.5">
              {NOT_INCLUDED.map((it, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <Warning size={12} weight="fill" className="text-[#B8862F] mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Keep this export complete — tucked-away agent prompt with a Copy button.
          Paste the copied text into the agent chat after new feature rounds/POs
          to re-audit + extend what this tool captures. */}
      <button
        type="button"
        onClick={() => setShowRefreshPrompt((v) => !v)}
        data-testid="migration-refresh-toggle"
        className="w-full flex items-center gap-1.5 text-sm font-medium text-[#4A5D4E] hover:text-[#3A4A3E] mb-3"
      >
        {showRefreshPrompt ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        <ArrowsClockwise size={14} weight="duotone" />
        Keep this export complete →
      </button>
      {showRefreshPrompt && (
        <div className="border hairline rounded-lg p-4 mb-4 bg-[#FBFAF6]" data-testid="migration-refresh-panel">
          <p className="text-[12px] text-[#6B6A68] leading-relaxed mb-2">
            After new feature rounds or purchases, some fresh data may not yet be captured by this
            export. Copy this prompt and paste it into the agent chat — it tells the agent exactly how
            to re-audit the tool and add anything new (never keys or money).
          </p>
          <div className="relative">
            <textarea
              readOnly
              value={CAPTURE_REFRESH_PROMPT}
              data-testid="migration-refresh-text"
              onFocus={(e) => e.target.select()}
              rows={8}
              className="w-full bg-white border hairline rounded-md p-3 pr-3 text-[11px] leading-relaxed text-[#3A3937] font-mono resize-y focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
          </div>
          <button
            type="button"
            onClick={copyRefreshPrompt}
            data-testid="migration-refresh-copy"
            className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#1C1B1A] text-white text-sm font-medium hover:bg-[#3A4A3E]"
          >
            {copiedPrompt ? <Check size={15} weight="bold" /> : <Copy size={15} weight="duotone" />}
            {copiedPrompt ? "Copied — paste it in chat" : "Copy prompt"}
          </button>
        </div>
      )}

      {/* Step 1 — Export (use in PREVIEW) */}
      <div className="border hairline rounded-lg p-4 mb-3 bg-[#FBFAF6]" data-testid="migration-export-card">
        <div className="text-[11px] uppercase tracking-[0.15em] text-[#6B6A68] mb-1">Step 1 · in preview</div>
        <div className="font-medium text-[#1C1B1A] mb-2">Export my silos + everything in them</div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={doExport}
            disabled={exporting}
            data-testid="migration-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4A5D4E] text-white text-sm font-medium hover:bg-[#3A4A3E] disabled:opacity-50"
          >
            <DownloadSimple size={15} weight="bold" /> {exporting ? "Bundling…" : "Export my data"}
          </button>
          {/* Feb 17, 2026 (Randy pt-migration-polish) — dry-run peek. Fetches
              exact counts + size WITHOUT downloading anything, so Randy can
              eyeball what's about to move before committing. */}
          <button
            type="button"
            onClick={doPreview}
            disabled={previewing || exporting}
            data-testid="migration-preview-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hairline text-sm text-[#4A5D4E] bg-white hover:bg-[#F4F3EE] disabled:opacity-50"
            title="Peek at what's in the export — counts and size — without downloading"
          >
            <MagnifyingGlass size={13} weight="duotone" />
            {previewing ? "Peeking…" : "Preview counts"}
          </button>
        </div>
        {preview && (
          <div className="mt-2 rounded-md border hairline bg-white p-2.5 text-[12px]" data-testid="migration-preview-result">
            <div className="text-[#1C1B1A] font-medium mb-1 flex items-center gap-2">
              <MagnifyingGlass size={12} weight="duotone" className="text-[#4A5D4E]" />
              This export would contain:
              <span className="text-[#4A5D4E]">{preview.silo_count} silo{preview.silo_count === 1 ? "" : "s"} · {preview.size_mb} MB</span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[#6B6A68]">
              {Object.entries(preview.counts || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <span key={k}>{v} {label(k)}</span>
              ))}
            </div>
          </div>
        )}
        <label className="mt-2.5 flex items-start gap-2 cursor-pointer" data-testid="migration-lite-toggle">
          <input type="checkbox" checked={liteExport} onChange={(e) => setLiteExport(e.target.checked)}
            className="mt-0.5" data-testid="migration-lite-checkbox" />
          <span className="text-[12px] text-[#1C1B1A] leading-relaxed">
            <strong>Lite export</strong> — skips the images (face + silo art), member avatars and saved voice clips.
            <span className="block text-[#6B6A68]">Much smaller and faster. Those items can always be
            regenerated, so this is perfect for a quick config-only snapshot; use the full export when
            you want everything to travel.</span>
          </span>
        </label>
        {lastExport && (
          <div className="mt-2 text-[12px] text-[#2F6B3A] flex flex-wrap gap-x-3 gap-y-0.5" data-testid="migration-export-result">
            <span className="flex items-center gap-1"><CheckCircle size={12} weight="duotone" /> Downloaded{lastSize != null ? ` (${lastSize} MB)` : ""}:</span>
            {Object.entries(lastExport).filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k}>{v} {label(k)}</span>
            ))}
          </div>
        )}
      </div>

      {/* Step 2 — Import (use in PRODUCTION) */}
      <div className="border hairline rounded-lg p-4 mb-3 bg-[#FBFAF6]" data-testid="migration-import-card">
        <div className="text-[11px] uppercase tracking-[0.15em] text-[#6B6A68] mb-1">Step 2 · in production (after deploy)</div>
        <div className="font-medium text-[#1C1B1A] mb-2">Import that file here</div>
        <input ref={fileRef} type="file" accept="application/json,.json" onChange={onFilePicked} className="hidden" data-testid="migration-import-file" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          data-testid="migration-import-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 border-[#4A5D4E] text-[#4A5D4E] text-sm font-medium hover:bg-[#4A5D4E] hover:text-white disabled:opacity-50"
        >
          <UploadSimple size={15} weight="bold" /> {importing ? (importProgress || "Importing…") : "Choose export file & import"}
        </button>
        {lastImport && (
          <div className="mt-2 text-[12px]" data-testid="migration-import-result">
            <div className="text-[#2F6B3A] flex items-center gap-1"><CheckCircle size={12} weight="duotone" /> Imported:</div>
            <div className="text-[#1C1B1A] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
              {Object.entries(lastImport.imported || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                <span key={k}>{v} {label(k)}</span>
              ))}
            </div>
            {lastImport.errors?.length > 0 && (
              <ul className="mt-1 text-[#C16E5A] list-disc ml-4">
                {lastImport.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Feb 2026 (Randy pt-prod-email-reminder-actionable · iter168) —
          Production .env reminder with EXACT STEPS. This is a MANUAL
          one-time step at Go-Live because env vars live on the hosting
          platform's box, NOT in the database — the migration import
          can't touch them. Placed right after the Import step + before
          the Keys note so it's the LAST thing an admin reads. */}
      <div
        data-testid="migration-prod-email-reminder"
        className="border hairline rounded-lg p-4 mb-3 bg-[#FBE9E1] border-[#C16E5A]/40"
      >
        <div className="flex items-start gap-2 mb-2">
          <Warning size={16} weight="fill" className="text-[#B54848] mt-0.5 shrink-0" />
          <div className="text-[12px] text-[#8C4A2E] leading-relaxed">
            <strong>Manual one-time step at Go-Live:</strong> turn on outbound emails
            in production. Preview + dev pods ship with <code className="mx-1 px-1 py-0.5 bg-white/70 rounded font-mono text-[11px]">EMAIL_DISABLED=1</code>
            (so tests don't spam anyone). If that same value ships to prod, welcome/verification/receipt
            emails <em>silently no-op</em> — no error, no log entry a user would see. Env vars live on
            your hosting platform's box (Emergent / Vercel / Railway / etc.), NOT in the database, so
            the Migration Import tool <strong>can't fix this for you</strong>.
          </div>
        </div>
        <ol className="ml-6 text-[12px] text-[#8C4A2E] leading-relaxed list-decimal space-y-1" data-testid="migration-prod-email-steps">
          <li>Open your production hosting platform's dashboard for the <strong>backend</strong> service (on Emergent this is the deployed pod's "Environment" panel; on Vercel/Railway/Fly it's under the project's Env Vars / Config Vars section).</li>
          <li>Find the entry <code className="mx-1 px-1 py-0.5 bg-white/70 rounded font-mono text-[11px]">EMAIL_DISABLED</code>. Either <strong>delete it</strong> or change its value from <code className="mx-0.5 px-1 py-0.5 bg-white/70 rounded font-mono text-[11px]">1</code> to <code className="mx-0.5 px-1 py-0.5 bg-white/70 rounded font-mono text-[11px]">0</code>.</li>
          <li>Save changes and <strong>redeploy or restart the backend service</strong> so the new value is picked up. (Emergent auto-restarts on env change; some platforms don't — check yours.)</li>
          <li>Once live, the Super Admin → Integrity Checks panel's new "Email delivery" row will flip green. If it stays red, the env var didn't get through — recheck Step 1-3.</li>
        </ol>
      </div>

      {/* Keys note */}
      <div className="border hairline rounded-lg p-4 bg-[#F4F6F2] flex items-start gap-2" data-testid="migration-keys-note">
        <Key size={16} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
        <div className="text-[12px] text-[#1C1B1A] leading-relaxed">
          <strong>About your keys:</strong> they are <strong>never exported and never overwritten</strong>.
          If your production already has working keys (it does), there's <strong>nothing to do</strong> —
          they stay exactly as they are. Only if a brand-new production has no keys yet would you re-enter
          them once (Account &amp; Billing for your own key; Super Admin for the platform key).
        </div>
      </div>

      <div className="mt-3 text-[11px] text-[#6B6A68] flex items-start gap-1.5" data-testid="migration-caveat">
        <Warning size={12} weight="fill" className="text-[#B8862F] mt-0.5 shrink-0" />
        <span>
          The export is a snapshot at click-time — export right before you deploy to catch your latest changes.
          Import is additive (it adds/updates by silo, it doesn't wipe anything else).
        </span>
      </div>
    </div>
  );
}
